import { z } from "zod";
import { MAX_QYOO_ID_NUMBER } from "../domain/qyoo-id.js";

export const QYOO_MANIFEST_SCHEMA = "qyoo.experience/1" as const;
export const MAX_MANIFEST_BYTES = 262_144;

function isHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      url.hostname.length > 0 &&
      url.username.length === 0 &&
      url.password.length === 0
    );
  } catch {
    return false;
  }
}

function graphemeLength(value: string): number {
  return [...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(value)].length;
}

const boundedText = (maximum: number, allowEmpty: boolean) =>
  z.string().refine(
    (value) => (allowEmpty || value.trim().length > 0) && graphemeLength(value) <= maximum,
    `must contain at most ${maximum} characters`
  );

const httpsUrl = z.url().regex(/^https:\/\/[^@\s]+$/).refine(isHttpsUrl, "must be an HTTPS URL without credentials");
const color = z.string().regex(/^#[0-9a-fA-F]{6}$/, "must be #RRGGBB");
const capability = z.enum(["shake", "motion", "share", "haptic", "openExternal"]);
const capabilities = z.array(capability).superRefine((values, context) => {
  if (new Set(values).size !== values.length) {
    context.addIssue({ code: "custom", message: "capabilities must not repeat" });
  }
});
const noCapabilities = capabilities.length(0);

const presentation = z
  .object({
    title: boundedText(120, false),
    subtitle: boundedText(240, true),
    icon: z
      .string()
      .refine((value) => value.startsWith("sf:") || isHttpsUrl(value), "invalid icon")
      .nullable()
      .optional(),
    background: color,
    foreground: color
  })
  .strict();

const relativeAnchor = z
  .object({
    type: z.literal("qyoo"),
    scaleMode: z.literal("relative")
  })
  .strict();

const knownWidthAnchor = z
  .object({
    type: z.literal("qyoo"),
    scaleMode: z.literal("knownPhysicalWidth"),
    physicalWidthMeters: z.number().finite().min(0.005).max(10)
  })
  .strict();

const experience = z.discriminatedUnion("type", [
  z.object({ type: z.literal("web"), url: httpsUrl, capabilities: noCapabilities }).strict(),
  z
    .object({ type: z.literal("interactiveWeb"), url: httpsUrl, capabilities })
    .strict(),
  z.object({ type: z.literal("redirect"), url: httpsUrl, capabilities: noCapabilities }).strict(),
  z
    .object({
      type: z.literal("nativeAction"),
      action: z.enum(["showPayload", "showIntro"]),
      capabilities: noCapabilities
    })
    .strict(),
  z
    .object({
      type: z.literal("ar"),
      scene: z.never(),
      anchor: z.union([relativeAnchor, knownWidthAnchor]),
      capabilities: noCapabilities
    })
    .strict()
]);

export const qyooExperienceManifestV1Schema = z
  .object({
    schema: z.literal(QYOO_MANIFEST_SCHEMA),
    qyoo: z.number().int().min(0).max(MAX_QYOO_ID_NUMBER),
    revision: z.number().int().positive(),
    presentation,
    experience
  })
  .strict();

// v2 is additive; v1 AR URLs are rejected because no v1 native scene loader existed.
const asset = z.object({
  url: httpsUrl,
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  mediaType: z.enum(["model/vnd.usdz+zip", "audio/wav", "image/png", "image/jpeg"]),
  byteLength: z.number().int().min(1).max(20_971_520)
}).strict();
const vector = z.tuple([z.number().finite().min(-2).max(2), z.number().finite().min(-2).max(2), z.number().finite().min(-2).max(2)]);
const nativeContent = z.discriminatedUnion("type", [
  z.object({ type: z.literal("proceduralComponent"), component: z.literal("qyoo.qyoob/1"),
    color, material: z.enum(["matte", "metallic"]), eyes: z.enum(["none", "two"])
  }).strict(),
  z.object({ type: z.literal("remoteModel"), asset: asset.extend({mediaType: z.literal("model/vnd.usdz+zip")}) }).strict()
]);
const nativeExperience = z.object({
  type: z.literal("ar"),
  capabilities: z.array(z.enum(["beaconTransmit", "beaconReceive"])).max(2),
  content: nativeContent,
  anchor: z.union([
    relativeAnchor.extend({attachmentMode: z.enum(["worldAnchored", "carrierAttached"])}),
    knownWidthAnchor.extend({attachmentMode: z.enum(["worldAnchored", "carrierAttached"])})
  ]),
  registration: z.object({pivot: z.enum(["center", "bottomCenter"]), offset: vector}).strict(),
  sizing: z.object({scaleRatio: z.number().finite().min(0.1).max(2)}).strict(),
  orientation: z.object({facing: z.enum(["anchor", "cameraAtPlacement"]), yawDegrees: z.number().finite().min(-180).max(180)}).strict(),
  animation: z.object({profile: z.enum(["none", "qyoobIdle", "spin"]), speed: z.number().finite().min(0.1).max(2)}).strict(),
  audio: z.object({asset: asset.extend({mediaType: z.literal("audio/wav"), byteLength: z.number().int().min(1).max(5_242_880)}), volume: z.number().finite().min(0).max(1), trigger: z.enum(["onLoad", "beacon"])}).strict().optional(),
  beacon: z.object({
    transmit: z.object({major: z.number().int().min(0).max(65535), minor: z.number().int().min(0).max(65535)}).strict().optional(),
    receive: z.object({qyoo: z.number().int().min(0).max(MAX_QYOO_ID_NUMBER), major: z.number().int().min(0).max(65535).optional(), minor: z.number().int().min(0).max(65535).optional()}).strict().optional()
  }).strict().optional(),
  eventBindings: z.array(z.object({event: z.enum(["qyoo:beaconEntered", "qyoo:beaconExited"]), action: z.enum(["playAudio", "show", "hide"]), cooldownSeconds: z.number().finite().min(1).max(60)}).strict()).max(4)
}).strict().superRefine((value, ctx) => {
  const fail = (message: string) => ctx.addIssue({code: "custom", message});
  if (new Set(value.capabilities).size !== value.capabilities.length) fail("duplicate capability");
  if (value.capabilities.includes("beaconTransmit") !== !!value.beacon?.transmit || value.capabilities.includes("beaconReceive") !== !!value.beacon?.receive) fail("beacon capability and definition must match");
  if (!!value.beacon !== (value.capabilities.length > 0)) fail("empty beacon definition");
  if (value.beacon?.receive?.minor !== undefined && value.beacon.receive.major === undefined) fail("minor requires major");
  if (value.eventBindings.length && !value.beacon?.receive) fail("bindings require beacon receive");
  if (new Set(value.eventBindings.map(x => x.event)).size !== value.eventBindings.length) fail("one binding per event");
  if (value.eventBindings.some(x => x.action === "playAudio") && value.audio?.trigger !== "beacon") fail("playAudio requires beacon audio");
  if (value.audio?.trigger === "beacon" && !value.eventBindings.some(x => x.action === "playAudio")) fail("beacon audio requires binding");
  if (value.anchor.scaleMode === "knownPhysicalWidth" && (value.anchor.physicalWidthMeters * value.sizing.scaleRatio < 0.005 || value.anchor.physicalWidthMeters * value.sizing.scaleRatio > 2)) fail("sized model must be 0.005–2 meters");
  if (value.animation.profile === "none" && value.animation.speed !== 1) fail("none animation requires speed 1");
  if (value.animation.profile === "qyoobIdle" && value.content.type !== "proceduralComponent") fail("qyoobIdle requires explicit qyoob");
  if (value.anchor.attachmentMode === "carrierAttached" && value.orientation.facing !== "anchor") fail("carrier content must face anchor");
});
export const qyooExperienceManifestV2Schema = z.object({
  schema: z.literal("qyoo.experience/2"),
  qyoo: z.number().int().min(0).max(MAX_QYOO_ID_NUMBER),
  revision: z.number().int().positive(),
  runtime: z.object({id: z.literal("qyoo.ios.native"), minimumVersion: z.literal("2.0.0")}).strict(),
  presentation: presentation.extend({artwork: asset.extend({mediaType: z.enum(["image/png", "image/jpeg"]), byteLength: z.number().int().min(1).max(2_097_152)}).optional()}),
  experience: nativeExperience
}).strict().superRefine((value, ctx) => {
  if (value.presentation.artwork && value.presentation.icon) ctx.addIssue({code:"custom", message:"choose icon or verified artwork, not both"});
});
export const qyooExperienceManifestSchema = z.union([qyooExperienceManifestV1Schema, qyooExperienceManifestV2Schema]);

export type QyooExperienceManifest = z.infer<typeof qyooExperienceManifestSchema>;
export type QyooExperienceType = QyooExperienceManifest["experience"]["type"];

export class ManifestValidationError extends Error {
  readonly issues: string[];

  constructor(issues: string[]) {
    super(`Invalid Qyoo Experience Manifest: ${issues.join("; ")}`);
    this.name = "ManifestValidationError";
    this.issues = issues;
  }
}

export function validateManifest(value: unknown, expectedId?: number): QyooExperienceManifest {
  const result = qyooExperienceManifestSchema.safeParse(value);
  if (!result.success) {
    throw new ManifestValidationError(
      result.error.issues.map((issue) => `${issue.path.join(".") || "manifest"}: ${issue.message}`)
    );
  }
  if (expectedId !== undefined && result.data.qyoo !== expectedId) {
    throw new ManifestValidationError([
      `qyoo: expected ${expectedId}, received ${result.data.qyoo}`
    ]);
  }
  const encodedBytes = Buffer.byteLength(JSON.stringify(result.data), "utf8");
  if (encodedBytes > MAX_MANIFEST_BYTES) {
    throw new ManifestValidationError([`manifest exceeds ${MAX_MANIFEST_BYTES} bytes`]);
  }
  return result.data;
}

export function parseManifestJson(data: Uint8Array, expectedId?: number): QyooExperienceManifest {
  if (data.byteLength > MAX_MANIFEST_BYTES) {
    throw new ManifestValidationError([`manifest exceeds ${MAX_MANIFEST_BYTES} bytes`]);
  }
  let value: unknown;
  try {
    value = JSON.parse(Buffer.from(data).toString("utf8"));
  } catch {
    throw new ManifestValidationError(["manifest is malformed JSON"]);
  }
  return validateManifest(value, expectedId);
}
